import { ArrowUturnLeftIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import { useState, type KeyboardEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import AppButton from "../../ui/buttons/AppButton";
import IconButton from "../../ui/buttons/IconButton";
import { changeToneClass, type ChangeKind } from "../../ui/styles/changeStyles";
import { classNames } from "../../ui/styles/classNames";
import { FORM_COMPACT_CONTROL_CLASS } from "../../ui/styles/formStyles";

interface InlineTextEditProps {
  /** The text as saved; empty for something new. */
  value: string;
  /** The unsaved new text, if this was edited. */
  staged: string | undefined;
  /** Why the unsaved edit can't be saved as it stands, or `null`. */
  problem: string | null;
  /** Whether this is the one thing the session is editing. */
  editing: boolean;
  onOpen: () => void;
  /** Stop editing, keeping what was typed. */
  onClose: () => void;
  /** Stop editing and go back to before it opened. */
  onCancel: () => void;
  /** The typed text, to make it the unsaved edit. */
  onChange: (text: string) => void;
  /** No edit any more: back to the saved text. */
  onUnstage: () => void;
  /** The input's name, and what the edit button says to screen readers. */
  label: string;
  multiline?: boolean;
  /** No edit button: someone else's, an older version, or a save running. */
  readOnly?: boolean;
  /** How the saved text shows; the plain text if not given. */
  children?: ReactNode;
  /** Beside the text in every mode, such as its language. */
  aside?: ReactNode;
  /** Beside the text field while editing, such as a language field for something new. */
  editorAside?: ReactNode;
}

/**
 * One text edited where it is shown (F96, as v1 edited annotations in place). The session
 * (`useEditSession`) owns the edit: what is typed is the unsaved edit at once, so Save, Discard
 * and the leave guard see it while the field is open. Done or Enter (Ctrl+Enter in a long text)
 * closes the field, Cancel or Escape goes back to before it opened. An unsaved edit shows the
 * old text struck through and the new one marked, with Undo, and its problem if it has one.
 */
export default function InlineTextEdit(props: InlineTextEditProps) {
  const { value, staged, problem, editing, onOpen, onUnstage, label, readOnly = false, children, aside } = props;
  const { t } = useTranslation();
  if (editing) return <InlineTextField {...props} initial={staged ?? value} />;
  const change: ChangeKind = value === "" ? "added" : "changed";
  return (
    <div className="space-y-1">
      <div className="flex min-w-0 flex-wrap items-start gap-2">
        {staged === undefined ? (
          <span className="min-w-0 flex-1 whitespace-pre-line break-words">{children ?? value}</span>
        ) : (
          <span className="min-w-0 flex-1 space-x-1 whitespace-pre-line break-words">
            {value === "" ? null : (
              <del className={classNames(changeToneClass("removed"), "rounded px-1")}>
                <span className="sr-only">{t("common.changes.removed")} </span>
                {value}
              </del>
            )}
            <ins className={classNames(changeToneClass(change), "rounded px-1 no-underline")}>
              <span className="sr-only">{t(`common.changes.${change}`)} </span>
              {staged}
            </ins>
          </span>
        )}
        {aside}
        {readOnly ? null : (
          <span className="inline-flex shrink-0 items-center">
            <IconButton label={t("editing.edit", { text: label })} className="h-6 w-6" onClick={onOpen}>
              <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
            </IconButton>
            {staged === undefined ? null : (
              <IconButton label={t("editing.undo", { text: label })} className="h-6 w-6" onClick={onUnstage}>
                <ArrowUturnLeftIcon className="h-4 w-4" aria-hidden="true" />
              </IconButton>
            )}
          </span>
        )}
      </div>
      {problem !== null ? <p className="app-error-text text-xs">{problem}</p> : null}
    </div>
  );
}

/** The open field; the text it holds is the session's edit as typed. */
function InlineTextField({ initial, value, problem, onClose, onCancel, onChange, onUnstage, label, multiline = false, editorAside }: InlineTextEditProps & { initial: string }) {
  const { t } = useTranslation();
  const [text, setText] = useState(initial);
  const change = (next: string) => {
    setText(next);
    // Back to the saved text is no edit.
    if (value !== "" && next.trim() === value.trim()) onUnstage();
    else onChange(next);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onCancel();
    } else if (event.key === "Enter" && !event.nativeEvent.isComposing && (!multiline || event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      onClose();
    }
  };
  const field = {
    "aria-label": label,
    "aria-invalid": problem !== null,
    className: classNames(FORM_COMPACT_CONTROL_CLASS, "min-w-0 flex-1"),
    value: text,
    autoFocus: true,
    onKeyDown,
    onChange: (event: { target: { value: string } }) => {
      change(event.target.value);
    },
  };
  return (
    <div className="w-full space-y-1">
      <div className="flex flex-wrap items-start gap-2">
        {multiline ? <textarea {...field} rows={3} /> : <input {...field} />}
        {editorAside}
        <AppButton variant="primary" size="xs" onClick={onClose}>
          {t("editing.keep")}
        </AppButton>
        <AppButton variant="ghost" size="xs" onClick={onCancel}>
          {t("common.cancel")}
        </AppButton>
      </div>
      {problem !== null ? <p className="app-error-text text-xs">{problem}</p> : null}
    </div>
  );
}
